import { WithTheme } from '@issuemanager/apps/client/theme';
import {
  Box,
  Chip,
  Tab,
  Tabs,
  Tooltip,
  styled,
  type ChipProps,
  type TabsProps,
} from '@mui/material';
import React, { type SyntheticEvent } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export type HeaderTab = {
  path: string;
  label: string;
  disabled?: boolean;
  tooltip?: string;
  count?: number;
};

export interface HeaderTabsProps {
  tabs: HeaderTab[];
}

const StyledTabs = styled(Tabs)<WithTheme<TabsProps>>(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  backgroundColor: theme.tokens.semantic.color.background.container.value,
}));

const StyledTab = styled(Tab)<WithTheme<TabsProps>>(({ theme }) => ({
  color: theme.tokens.semantic.color.text.disabled.value,
  ':hover': {
    color: theme.tokens.semantic.color.text.disabled.value,
  },
}));

const StyledChip = styled(Chip)<WithTheme<ChipProps>>(({ theme }) => ({
  padding: `0 ${theme.spacing(0.5)}`,
  borderRadius: theme.shape.borderRadius / 2,
  height: theme.spacing(2),
}));

export function HeaderTabs(props: HeaderTabsProps): React.JSX.Element | null {
  const { tabs = [] } = props;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleChange = (
    event: SyntheticEvent<Element, Event>,
    newId: string,
  ): void => {
    event.preventDefault();
    const targetTab = tabs.find((tab) => tab.path === newId);
    if (targetTab && !targetTab.disabled) {
      navigate(newId);
    }
  };

  if (!tabs.length) return null;

  return (
    <StyledTabs
      className='atlas-size-large'
      onChange={handleChange}
      scrollButtons='auto'
      variant='standard'
      defaultValue={0}
      value={pathname}
    >
      {tabs.map((tab, index) => {
        const tabProps = {
          key: tab.label,
          id: `tab-${tab.path}-${index}`,
          'aria-controls': `tabpanel-${index}`,
          value: tab.path,
          label:
            tab.count !== undefined ? (
              <Box display='flex' alignItems='center' gap={1}>
                {tab.label}
                <StyledChip label={tab.count} size='medium' />
              </Box>
            ) : (
              tab.label
            ),
          'data-testid': `tab-${tab.path}`,
        };

        const TabComponent = tab.disabled ? StyledTab : Tab;
        const tabElement = <TabComponent {...tabProps} />;

        return tab.disabled && tab.tooltip ? (
          <Tooltip key={tab.label} title={tab.tooltip}>
            {tabElement}
          </Tooltip>
        ) : (
          tabElement
        );
      })}
    </StyledTabs>
  );
}
